import { zodResolver } from '@hookform/resolvers/zod'
import { KeyRound, UserCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { RadioGroup } from '@/components/ui/RadioGroup'
import { Select } from '@/components/ui/Select'
import { humanize } from '@/utils/format'
import { useChangeStaffStatus, useCreateStaffAccount } from '../api'
import { staffAccountSchema, type StaffAccountInput } from '../schemas'
import { STAFF_STATUSES, type StaffMember, type StaffStatus } from '../types'

/** Dialogs here are mounted only while open, so their state starts fresh each time. */
interface Props {
  member: StaffMember
  open: boolean
  onOpenChange: (open: boolean) => void
}

const help: Record<StaffStatus, string> = {
  active: 'Working at the school.',
  on_leave: 'Temporarily away (long leave, sabbatical).',
  resigned: 'Left voluntarily. Portal access is disabled.',
  terminated: 'Employment ended by the school. Portal access is disabled.',
}

export function StaffStatusDialog({ member, open, onOpenChange }: Props) {
  const { t } = useTranslation('staff')
  const change = useChangeStaffStatus()
  const [status, setStatus] = useState<StaffStatus>(member.status)

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<UserCheck />}
      title={t('Change employment status')}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('Cancel')}
          </Button>
          <Button
            variant={status === 'terminated' ? 'danger' : 'primary'}
            disabled={status === member.status}
            loading={change.isPending}
            onClick={() =>
              change.mutate(
                { id: member.id, status },
                {
                  onSuccess: () => {
                    toast.success(t('Status updated'))
                    onOpenChange(false)
                  },
                  onError: (e) => toast.error(t('Could not update status'), toApiError(e).message),
                },
              )
            }
          >
            {t('Update status')}
          </Button>
        </>
      }
    >
      <RadioGroup
        aria-label={t('Employment status')}
        appearance="cards"
        value={status}
        onValueChange={(v) => setStatus(v as StaffStatus)}
        options={STAFF_STATUSES.map((s) => ({ value: s, label: humanize(s), description: t(help[s]) }))}
      />
    </Dialog>
  )
}

const roleOptions = [
  { value: 'teacher', label: 'Teacher — staff portal' },
  { value: 'accountant', label: 'Accountant — admin (finance) + staff portal' },
  { value: 'librarian', label: 'Librarian — admin (library) + staff portal' },
  { value: 'receptionist', label: 'Receptionist — admin (front desk) + staff portal' },
  { value: 'admin', label: 'Administrator — full admin console' },
]

export function StaffAccountDialog({ member, open, onOpenChange }: Props) {
  const { t } = useTranslation('staff')
  const create = useCreateStaffAccount()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<StaffAccountInput>({
    resolver: zodResolver(staffAccountSchema),
    defaultValues: { role: 'teacher', email: member.email ?? '', password: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    try {
      await create.mutateAsync({ id: member.id, ...values })
      toast.success(t('Login created'), t('{{email}} can now sign in.', { email: values.email }))
      onOpenChange(false)
    } catch (e) {
      applyServerErrors(e, setError, ['role', 'email', 'password'])
    }
  })

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<KeyRound />}
      title={t('Create a login')}
      description={t(
        'Give {{name}} access to the platform. Their role decides which portals and pages they can use.',
        { name: member.fullName },
      )}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form="staff-account-form" loading={isSubmitting}>
            {t('Create login')}
          </Button>
        </>
      }
    >
      <form id="staff-account-form" onSubmit={onSubmit} noValidate className="space-y-4">
        <FormField label={t('Role')} error={errors.role?.message} required>
          <Select options={roleOptions.map((o) => ({ ...o, label: t(o.label) }))} {...register('role')} />
        </FormField>
        <FormField label={t('Sign-in email')} error={errors.email?.message} required>
          <Input type="email" autoComplete="off" {...register('email')} />
        </FormField>
        <FormField
          label={t('Temporary password')}
          error={errors.password?.message}
          required
          hint={t('At least 8 characters.')}
        >
          <Input type="text" autoComplete="new-password" {...register('password')} />
        </FormField>
      </form>
    </Dialog>
  )
}
